import FormRow from '@components/form/FormRow';
import { useDispatch, useSelector } from 'react-redux';

import ActionButton from '@components/button/ActionButton';
import ButtonRow from '@components/button/ButtonRow';
import MiniSelect from '@components/form/MiniSelect';
import TextInput from '@components/form/TextInput';
import useForm from '@hooks/useForm';
import { fetchSyncsSearch, SearchFormData } from '@actions/syncs';
import { dataSearchOptions, syncTypeOptions, isSuccessOptions } from '@constants/enums/syncEnums';
import { getSyncsIsFetching } from '@selectors/syncs';
import { getFormError } from '@selectors/fieldErrors';
import { useCallback, useEffect } from 'react';
import { clearFormError } from '@actions/fieldErrors';
import Radio from '@components/form/Radio';
import dayjs from 'dayjs';
import DatePicker from '@components/form/DatePicker';
import { DATE_FORMAT } from '@constants/dates';

const DataSearchBox: React.FC = () => {
    const dispatch = useDispatch();
    const isFetching = useSelector(getSyncsIsFetching);
    const formError = useSelector(getFormError);

    const fromDate = dayjs().startOf('day').subtract(30, 'days').format(DATE_FORMAT);

    const [formData, handleChange] = useForm<SearchFormData>({
        searchTerm: '',
        nodeTitle: null,
        sync: null,
        isSuccess: 'true',
        fromDate: fromDate,
        toDate: null,
    });

    const clearError = useCallback(() => {
        dispatch(clearFormError());
    }, [dispatch]);

    useEffect(() => {
        return () => clearError();
    }, [dispatch, clearError]);

    return (
        <div className="table-search no-flex content-block">
            <form className="data-search-form flex-row" onSubmit={_handleSubmit}>
                <FormRow>
                    <MiniSelect
                        name="nodeTitle"
                        options={dataSearchOptions}
                        value={formData.nodeTitle ?? null}
                        onChange={handleChange}
                    />
                    <MiniSelect
                        name="sync"
                        options={syncTypeOptions}
                        value={formData.sync ?? null}
                        onChange={handleChange}
                    />
                </FormRow>
                <FormRow>
                    <p className="warning">
                        Setting a large date range could cause the search results to be slow.
                    </p>
                </FormRow>
                <FormRow>
                    <DatePicker
                        name="fromDate"
                        label="From"
                        value={formData.fromDate ? new Date(formData.fromDate) : null}
                        onChange={(name, val) => {
                            if (!val) {
                                handleChange(name, null);
                            } else {
                                const date = dayjs(val).format(DATE_FORMAT);

                                handleChange(name, date);
                            }
                        }}
                        placeholder="From Date"
                    />
                    <DatePicker
                        name="toDate"
                        label="To"
                        value={formData.toDate ? new Date(formData.toDate) : null}
                        onChange={(name, val) => {
                            if (!val) {
                                handleChange(name, null);
                            } else {
                                const date = dayjs(val).format(DATE_FORMAT);

                                handleChange(name, date);
                            }
                        }}
                        placeholder="To Date"
                    />
                </FormRow>
                <FormRow>
                    <Radio
                        name="isSuccess"
                        value={formData.isSuccess ?? null}
                        options={isSuccessOptions}
                        onChange={handleChange}
                    />
                    <TextInput
                        name="searchTerm"
                        value={formData.searchTerm ?? ''}
                        onChange={handleChange}
                        placeholder="Search..."
                    />

                    <ButtonRow alignment="right">
                        <ActionButton isPosting={isFetching}>Find changes</ActionButton>
                    </ButtonRow>
                </FormRow>
            </form>

            {!!formError && (
                <p className="form-generic-error" style={{ marginTop: '10px' }}>
                    {formError}
                </p>
            )}
        </div>
    );

    function _handleSubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();
        clearError();
        dispatch(fetchSyncsSearch(formData));
    }
};

export default DataSearchBox;
